import { Injectable, inject } from '@angular/core';

import { environment } from 'src/environments/environment';

import { AccountCreationSource, AccountStatus } from '@core/models/auth.enum';

import { AuthService } from './auth.service';
import { CurrentUserService } from './current-user.service';
import { MockDataService } from './mock-data.service';

interface MockJwtClaims {
  userid: string;
  companyid: string;
  email: string;
  fullname: string;
  role: string;
  phonenumber: string;
  userOrigin?: string;
  accountStatus: AccountStatus;
  emailVerified: boolean;
  phoneVerified: boolean;
  /** Unix seconds; omitted when the event has never occurred. */
  emailVerifiedAt?: number;
  phoneVerifiedAt?: number;
  creationSource: AccountCreationSource;
}

interface FailedAttemptRecord {
  count: number;
  firstFailureAt: number;
  lockedUntil?: number;
}

const MAX_FAILED_ATTEMPTS = 5;
const ATTEMPT_WINDOW_MS = 15 * 60 * 1000;

/** Thrown when a password attempt is rejected before lockout kicks in. */
export class InvalidCredentialsError extends Error {
  constructor(
    message: string,
    readonly attemptsRemaining: number
  ) {
    super(message);
    this.name = 'InvalidCredentialsError';
  }
}

/** Thrown when the 5-attempts-within-15-minutes threshold is exceeded. */
export class AccountLockedError extends Error {
  constructor(readonly unlocksAt: number) {
    super('Account temporarily locked.');
    this.name = 'AccountLockedError';
  }
}

/** Thrown when password is valid but the account has not completed verification (FR §4.3). */
export class AccountUnverifiedError extends Error {
  constructor() {
    super('Please verify your email to continue.');
    this.name = 'AccountUnverifiedError';
  }
}

/** Thrown when password is valid but the account is Disabled (FR §4.3). */
export class AccountDisabledError extends Error {
  constructor() {
    super('Your account is disabled. Contact support.');
    this.name = 'AccountDisabledError';
  }
}

/**
 * Dev-only auth simulator. Generates a JWT whose claims mirror the production
 * Company API response, wires up CurrentUserService signals, and sets the
 * access/refresh/session cookies via AuthService.
 */
@Injectable({ providedIn: 'root' })
export class DevAuthService {
  private readonly authService = inject(AuthService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly mockData = inject(MockDataService);

  constructor() {
    this.assertDevOnly();
  }

  private readonly failedAttempts = new Map<string, FailedAttemptRecord>();

  /**
   * Primary identifier is email (IMPLEMENTATION.md §0 / FRD §D.2).
   * `MockDataService.getConsignorByPhoneOrEmail` resolves seeded consignors by
   * email (case-insensitive), falling back to phone for dev convenience.
   * Always clears any prior session before creating the new one.
   */
  login(email: string): void {
    this.authService.clearAuthData();

    const consignor = this.mockData.getConsignorByPhoneOrEmail(email) ?? this.mockData.consignors()[0];

    this.issueSessionFor(consignor.id);
  }

  /**
   * FRD §D.3 — lockout after 5 failed attempts within 15 minutes.
   * Password `wrong` simulates failure. On success, failure counters reset.
   *
   * Throws `AccountLockedError` while locked or `InvalidCredentialsError`
   * for ordinary password mismatches. Both include the data the UI needs
   * to show remaining attempts or a countdown.
   */
  attemptLogin(email: string, password: string): void {
    const key = email.trim().toLowerCase();
    const now = Date.now();
    const record = this.readRecord(key, now);

    if (record?.lockedUntil && now < record.lockedUntil) {
      throw new AccountLockedError(record.lockedUntil);
    }

    if (password === 'wrong') {
      const updated = this.registerFailure(key, record, now);
      if (updated.lockedUntil) {
        throw new AccountLockedError(updated.lockedUntil);
      }
      throw new InvalidCredentialsError(
        'Invalid email or password.',
        Math.max(0, MAX_FAILED_ATTEMPTS - updated.count)
      );
    }

    // Credentials OK — gate by account status before issuing a session (FR §4.3).
    const consignor = this.mockData.getConsignorByPhoneOrEmail(email);
    if (consignor?.accountStatus === AccountStatus.Disabled) {
      this.failedAttempts.delete(key);
      throw new AccountDisabledError();
    }
    if (consignor?.accountStatus === AccountStatus.PendingVerification) {
      this.failedAttempts.delete(key);
      throw new AccountUnverifiedError();
    }

    this.failedAttempts.delete(key);
    this.login(email);
  }

  private readRecord(key: string, now: number): FailedAttemptRecord | undefined {
    const record = this.failedAttempts.get(key);
    if (!record) return undefined;
    // Expired lockout — clear it so the next attempt starts fresh.
    if (record.lockedUntil && now >= record.lockedUntil) {
      this.failedAttempts.delete(key);
      return undefined;
    }
    // Window expired without a lockout — clear failure counters.
    if (!record.lockedUntil && now - record.firstFailureAt > ATTEMPT_WINDOW_MS) {
      this.failedAttempts.delete(key);
      return undefined;
    }
    return record;
  }

  private registerFailure(key: string, existing: FailedAttemptRecord | undefined, now: number): FailedAttemptRecord {
    const record: FailedAttemptRecord = existing ?? { count: 0, firstFailureAt: now };
    record.count += 1;
    if (record.count === 1) record.firstFailureAt = now;
    if (record.count >= MAX_FAILED_ATTEMPTS) {
      record.lockedUntil = now + ATTEMPT_WINDOW_MS;
    }
    this.failedAttempts.set(key, record);
    return record;
  }

  /**
   * Register a brand-new consignor from the self-registration flow.
   * Adds them to the mock seed and logs them in.
   *
   * Propagates {@link DuplicateEmailError} (from MockDataService) when the
   * email is already taken — caller surfaces the FR-mandated message.
   */
  register(info: {
    fullName: string;
    email: string;
    phoneNumber: string;
    businessName?: string;
    mailingAddress: string;
  }): void {
    this.authService.clearAuthData();
    const created = this.mockData.createConsignor(info);
    this.issueSessionFor(created.id);
  }

  logout(): void {
    this.authService.clearAuthData();
    this.currentUser.clear();
  }

  /**
   * Issue a session for a consignor by id (used by flows that already have the
   * authenticated subject, e.g. staff-activation completion — FR §1.4).
   */
  loginAsConsignor(consignorId: string): void {
    this.authService.clearAuthData();
    this.issueSessionFor(consignorId);
  }

  private issueSessionFor(consignorId: string): void {
    const consignor = this.mockData.getConsignor(consignorId) ?? this.mockData.consignors()[0];

    const token = this.createMockJwt({
      userid: consignor.id,
      companyid: '1',
      email: consignor.email,
      fullname: consignor.fullName,
      role: 'consignor',
      phonenumber: consignor.phoneNumber,
      userOrigin: 'External',
      accountStatus: consignor.accountStatus ?? AccountStatus.Active,
      emailVerified: consignor.emailVerified ?? true,
      phoneVerified: consignor.phoneVerified ?? true,
      emailVerifiedAt: toEpochSeconds(consignor.emailVerifiedAt),
      phoneVerifiedAt: toEpochSeconds(consignor.phoneVerifiedAt),
      creationSource: consignor.creationSource ?? AccountCreationSource.Self
    });

    this.authService.setTokens(token, `refresh-${Date.now()}`, `session-${Date.now()}`);
    this.currentUser.setPortal('consignor');
    this.currentUser.setConsignorId(consignor.id);
    this.currentUser.setTax1099Status(consignor.tax.status);
    this.currentUser.setTcGate(consignor.tc);
  }

  private assertDevOnly(): void {
    if (environment.production) {
      throw new Error('DevAuthService must not be used in production — use real authentication.');
    }
  }

  /**
   * Produces a base64url-encoded JWT that jwt-decode can parse. The signature
   * is a stub — real validation happens server-side.
   */
  private createMockJwt(claims: MockJwtClaims): string {
    const now = Math.floor(Date.now() / 1000);
    const header = { alg: 'HS256', typ: 'JWT' };
    const payload: Record<string, unknown> = {
      sub: claims.userid,
      'custom:userid': claims.userid,
      'custom:companyid': claims.companyid,
      'custom:email': claims.email,
      'custom:fullname': claims.fullname,
      'custom:role': [claims.role],
      'custom:phonenumber': claims.phonenumber,
      'custom:usertype': 'AppUser',
      'custom:userorigin': claims.userOrigin ?? 'External',
      'custom:timezonecode': 'UTC',
      'custom:mustchangepasswordonlogin': 'False',
      'custom:accountstatus': String(claims.accountStatus),
      'custom:emailverified': String(claims.emailVerified),
      'custom:phoneverified': String(claims.phoneVerified),
      'custom:creationsource': String(claims.creationSource),
      iss: 'dev-auth-simulator',
      aud: 'consignor-portal',
      iat: now,
      nbf: now,
      exp: now + 24 * 60 * 60
    };
    if (claims.emailVerifiedAt != null) payload['custom:emailverifiedat'] = String(claims.emailVerifiedAt);
    if (claims.phoneVerifiedAt != null) payload['custom:phoneverifiedat'] = String(claims.phoneVerifiedAt);
    return `${encode(header)}.${encode(payload)}.dev-signature`;
  }
}

function encode(value: object): string {
  return btoa(JSON.stringify(value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function toEpochSeconds(iso: string | undefined): number | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}
